/* Mobile teacher: weekly availability + pending parent appointments. */
import { useCallback, useEffect, useState } from 'react';
import { mobileTeacherApi } from '../../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Check, X, Bell, Clock, CalendarCheck } from '@phosphor-icons/react';

const DAYS = [
  { dow: 0, name: 'Lundi' }, { dow: 1, name: 'Mardi' },
  { dow: 2, name: 'Mercredi' }, { dow: 3, name: 'Jeudi' },
  { dow: 4, name: 'Vendredi' }, { dow: 5, name: 'Samedi' },
  { dow: 6, name: 'Dimanche' },
];

const TeacherAvailability = ({ me, onNotifChange }) => {
  const [tab, setTab] = useState('appts'); // 'appts' | 'slots'
  const [appts, setAppts] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDow, setSavingDow] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        mobileTeacherApi.appointments(),
        mobileTeacherApi.availability(me.teacher_id),
      ]);
      setAppts(a.data.appointments || []);
      setPendingCount(a.data.pending || 0);
      if (onNotifChange) onNotifChange(a.data.pending || 0);
      setSlots(s.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [me.teacher_id, onNotifChange]);

  useEffect(() => { reload(); }, [reload]);

  const acceptAppt = async (id) => {
    try {
      await mobileTeacherApi.updateAppointment(id, { status: 'accepted' });
      toast.success('Rendez-vous accepté');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const rejectAppt = async (id) => {
    if (!window.confirm('Refuser ce rendez-vous ?')) return;
    try {
      await mobileTeacherApi.updateAppointment(id, { status: 'cancelled' });
      toast.success('Rendez-vous refusé');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const saveSlot = async (dow, form) => {
    setSavingDow(dow);
    try {
      await mobileTeacherApi.setAvailability({
        teacher_id: me.teacher_id,
        day_of_week: dow,
        start_time: form.start_time || '08:00',
        end_time: form.end_time || '17:00',
        slot_duration: parseInt(form.slot_duration || '30', 10),
      });
      toast.success(`✓ ${DAYS[dow].name} enregistré`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSavingDow(null); }
  };

  const pendings = appts.filter(a => a.status === 'pending');
  const accepteds = appts.filter(a => a.status === 'accepted');

  return (
    <div className="p-4 space-y-4" data-testid="teacher-availability">
      {/* Tab pills */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-1 grid grid-cols-2 text-sm">
        <button
          onClick={() => setTab('appts')}
          data-testid="tab-appts"
          className={`py-2 rounded flex items-center justify-center gap-1.5 ${
            tab === 'appts' ? 'bg-[#0052FF] text-white font-semibold' : 'text-[#64748B]'
          }`}
        >
          <Bell size={15} />
          Rendez-vous
          {pendingCount > 0 && (
            <span className="ml-1 text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5">
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('slots')}
          data-testid="tab-slots"
          className={`py-2 rounded flex items-center justify-center gap-1.5 ${
            tab === 'slots' ? 'bg-[#0052FF] text-white font-semibold' : 'text-[#64748B]'
          }`}
        >
          <Clock size={15} />
          Ma disponibilité
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
        </div>
      ) : tab === 'appts' ? (
        <div className="space-y-4">
          <ApptsSection title="En attente" testid="appts-pending" items={pendings}
            onAccept={acceptAppt} onReject={rejectAppt} emptyMsg="Aucune demande en attente."
          />
          <ApptsSection title="Confirmés" testid="appts-accepted" items={accepteds}
            emptyMsg="Aucun rendez-vous confirmé." confirmed
          />
        </div>
      ) : (
        <div className="space-y-2">
          {DAYS.map(d => {
            const existing = slots.find(s => Number(s.day_of_week) === d.dow);
            return (
              <SlotCard
                key={d.dow}
                day={d}
                existing={existing}
                saving={savingDow === d.dow}
                onSave={(form) => saveSlot(d.dow, form)}
              />
            );
          })}
          <p className="text-[11px] text-[#94A3B8] text-center pt-2">
            Le durée par créneau est en minutes (défaut 30 min).
          </p>
        </div>
      )}
    </div>
  );
};

const ApptsSection = ({ title, testid, items, onAccept, onReject, emptyMsg, confirmed }) => (
  <section data-testid={testid}>
    <h3 className="text-xs font-medium text-[#64748B] uppercase mb-2 flex items-center gap-2">
      <CalendarCheck size={13} /> {title} ({items.length})
    </h3>
    {items.length === 0 ? (
      <p className="text-sm text-[#94A3B8] py-4 text-center">{emptyMsg}</p>
    ) : (
      <ul className="space-y-2">
        {items.map(a => (
          <li key={a.id} className="bg-white rounded-lg border border-[#E2E8F0] p-3" data-testid={`appt-${a.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-semibold text-sm">
                  {a.parent_name} <span className="text-xs text-[#64748B] font-normal">(parent de {a.student_name})</span>
                </div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  📅 {a.date} à {a.time}
                </div>
                {a.reason && (
                  <div className="text-xs text-[#64748B] mt-1 italic">&laquo;{a.reason}&raquo;</div>
                )}
              </div>
              {confirmed ? (
                <span className="text-xs bg-[#DCFCE7] text-[#15803D] px-2 py-1 rounded-full font-medium">
                  Confirmé
                </span>
              ) : (
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => onAccept(a.id)}
                    className="h-9 w-9 rounded bg-[#10B981] text-white flex items-center justify-center"
                    title="Accepter"
                    data-testid={`accept-${a.id}`}
                  >
                    <Check size={16} weight="bold" />
                  </button>
                  <button
                    onClick={() => onReject(a.id)}
                    className="h-9 w-9 rounded bg-red-500 text-white flex items-center justify-center"
                    title="Refuser"
                    data-testid={`reject-${a.id}`}
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const SlotCard = ({ day, existing, saving, onSave }) => {
  const [enabled, setEnabled] = useState(Boolean(existing));
  const [start, setStart] = useState(existing?.start_time || '08:00');
  const [end, setEnd] = useState(existing?.end_time || '17:00');
  const [dur, setDur] = useState((existing?.slot_duration || 30).toString());

  useEffect(() => {
    if (existing) {
      setEnabled(true);
      setStart(existing.start_time || '08:00');
      setEnd(existing.end_time || '17:00');
      setDur((existing.slot_duration || 30).toString());
    }
  }, [existing]);

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid={`slot-day-${day.dow}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 accent-[#0052FF]"
              data-testid={`slot-toggle-${day.dow}`}
            />
            <span className="font-outfit font-semibold text-sm">{day.name}</span>
          </div>
          {enabled && (
            <Button
              onClick={() => onSave({ start_time: start, end_time: end, slot_duration: dur })}
              disabled={saving}
              size="sm"
              className="h-8 bg-[#0052FF] hover:bg-[#0040CC] text-xs"
              data-testid={`slot-save-${day.dow}`}
            >
              {saving ? '…' : 'Enregistrer'}
            </Button>
          )}
        </div>
        {enabled && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8]">Début</label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 text-sm" data-testid={`slot-start-${day.dow}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8]">Fin</label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 text-sm" data-testid={`slot-end-${day.dow}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8]">Durée (min)</label>
              <Input type="number" min="5" step="5" value={dur} onChange={(e) => setDur(e.target.value)} className="h-9 text-sm" data-testid={`slot-dur-${day.dow}`} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherAvailability;
